import React, { useEffect, useState, useRef, useCallback } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router-dom";
import { FaBell, FaTimes } from "react-icons/fa";
import api from "../../api.js";
import { useAuth } from "../../context/AuthContext.jsx";
import { useSocket } from "../../context/SocketContext.jsx";
dayjs.extend(relativeTime);

// Utility function for VAPID key conversion
const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const NotificationsBell = () => {
  const { user } = useAuth();
  const { socket, joinProfileRoom, leaveProfileRoom } = useSocket();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  // Safe read of Notification.permission (avoid errors during SSR/build)
  const notifPermission =
    typeof Notification !== "undefined" ? Notification.permission : "default";

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user?.uid) return;

    try {
      setIsLoading(true);
      const res = await api.get(`/notifications/${user.uid}?limit=20&page=1`);
      if (!mountedRef.current) return;

      const list = res.data?.notifications || [];
      setNotifications(list);
      setUnreadCount(list.filter((n) => !n.read).length);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [user]);

  // Socket and event listeners setup
  useEffect(() => {
    if (!user?.uid) return;

    fetchNotifications();

    // Join profile socket room to receive personal notifications
    try {
      joinProfileRoom(user.uid);
    } catch (e) {
      console.warn("Failed to join profile room", e);
    }

    const handleNotification = (payload) => {
      // Deduplication with proper unique identifier
      const uniqKey =
        payload.notificationId ||
        payload.replyId ||
        payload.id ||
        `live-${Date.now()}`;

      setNotifications((prev) => {
        const exists = prev.some(
          (n) =>
            n._id === uniqKey ||
            (n.payload &&
              (n.payload.notificationId === uniqKey ||
                n.payload.replyId === uniqKey ||
                n.payload.id === uniqKey))
        );
        if (exists) return prev;

        const newNotif = {
          _id: uniqKey,
          userId: user.uid,
          type: payload.type || "reply",
          payload,
          read: false,
          createdAt: payload.createdAt || new Date().toISOString(),
        };

        return [newNotif, ...prev.slice(0, 49)]; // Keep only 50 latest
      });

      setUnreadCount((c) => c + 1);

      // Show browser notification if supported and enabled
      if (
        pushEnabled &&
        "Notification" in window &&
        Notification.permission === "granted"
      ) {
        const title = "New Notification";
        const body =
          payload.type === "reply"
            ? `${payload.replierName || "Someone"} replied to your comment`
            : "You have a new notification";

        new Notification(title, { body, icon: "/icon-192.png" });
      }
    };

    socket?.on?.("notification", handleNotification);

    // Click outside and escape to close
    const handleEscapeKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    const handleDocumentClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      try {
        leaveProfileRoom(user.uid);
      } catch (e) {
        console.warn("Failed to leave profile room", e);
      }

      socket?.off?.("notification", handleNotification);
      document.removeEventListener("keydown", handleEscapeKey);
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, [user, socket, fetchNotifications, pushEnabled]);

  // Push support check and existing subscription detection
  useEffect(() => {
    if (!user?.uid) return;

    const supported = "serviceWorker" in navigator && "PushManager" in window;
    setPushSupported(supported);

    if (!supported) return;

    let mounted = true;

    const checkPushSubscription = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!mounted) return;

        if (!reg) {
          setPushEnabled(false);
          return;
        }

        const sub = await reg.pushManager.getSubscription();
        if (!mounted) return;

        setPushEnabled(!!sub);
      } catch (err) {
        console.error("Push support check failed", err);
      }
    };

    checkPushSubscription();

    return () => {
      mounted = false;
    };
  }, [user]);

  const toggleOpen = () => setIsOpen((prev) => !prev);

  const markAsRead = useCallback(
    async (id) => {
      try {
        // Optimistic update
        setNotifications((prev) =>
          prev.map((n) => (n._id === id ? { ...n, read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));

        await api.patch(`/notifications/${id}/read`);
      } catch (err) {
        console.error("Failed to mark notification read", err);
        // Revert optimistic update on error
        fetchNotifications();
      }
    },
    [fetchNotifications]
  );

  const markAllAsRead = useCallback(async () => {
    if (!user?.uid) return;

    try {
      await api.patch(`/notifications/user/${user.uid}/read-all`);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all read", err);
    }
  }, [user]);

  const enablePush = async () => {
    if (!user?.uid) {
      setPushError("Not authenticated");
      return;
    }

    setPushError(null);
    setPushLoading(true);

    try {
      // Register service worker if not registered
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register("/sw.js");
        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setPushError("Notification permission denied");
        setPushLoading(false);
        return;
      }

      // Get VAPID key from backend
      const vapidRes = await api.get(`/push/vapidPublicKey`);
      const vapidPublicKey = vapidRes.data?.publicKey;
      if (!vapidPublicKey) {
        throw new Error("No VAPID public key from server");
      }

      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // Send subscription to backend
      await api.post(`/push/subscribe`, {
        subscription,
        userId: user.uid,
      });

      setPushEnabled(true);
    } catch (err) {
      console.error("Failed to enable push", err);
      setPushError(err?.message || "Failed to enable push");
    } finally {
      setPushLoading(false);
    }
  };

  const handleNotificationClick = useCallback(
    (notification) => {
      try {
        const payload = notification.payload || {};

        // Handle different notification types
        if (payload.animeId) {
          // Build slug path if payload contains slug or fallback to id
          const slug = payload.animeTitle
            ? payload.animeTitle
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "")
            : "anime";
          navigate(`/anime/${slug}?animeID=${payload.animeId}`);
        }

        // Mark as read when clicking if unread
        if (!notification.read) {
          markAsRead(notification._id);
        }

        setIsOpen(false);
      } catch (e) {
        console.error("Error handling notification click", e);
      }
    },
    [markAsRead, navigate]
  );

  // Render notification content based on type
  const renderNotificationContent = (notification) => {
    const { type, payload } = notification;

    switch (type) {
      case "reply":
        return (
          <div>
            <strong className="text-blue-300">
              {payload.replierName || "Someone"}
            </strong>
            <span> replied: </span>
            <span className="text-gray-200">
              {payload.replyText?.slice(0, 120)}
              {payload.replyText?.length > 120 ? "..." : ""}
            </span>
          </div>
        );
      case "like":
        return (
          <div>
            <strong className="text-blue-300">
              {payload.likerName || "Someone"}
            </strong>
            <span> liked your comment</span>
          </div>
        );
      case "follow":
        return (
          <div>
            <strong className="text-blue-300">
              {payload.followerName || "Someone"}
            </strong>
            <span> started following you</span>
          </div>
        );
      default:
        return (
          <div className="text-gray-200">
            {type}: {JSON.stringify(payload)}
          </div>
        );
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={toggleOpen}
        className="relative text-white p-2 rounded-full hover:bg-gray-800 transition-all focus:outline-none focus:ring-2 focus:ring-blue-400"
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        <FaBell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 min-w-5 flex items-center justify-center px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto bg-darkGray border border-gray-700 rounded-lg shadow-lg z-50 p-2">
          <div className="flex justify-between items-center mb-2 px-2">
            <strong className="text-white">Notifications</strong>
            <div className="flex items-center gap-2">
              {pushSupported && !pushEnabled && (
                <button
                  onClick={enablePush}
                  disabled={pushLoading}
                  className="text-sm text-gray-300 hover:text-white disabled:opacity-50"
                  aria-label="Enable push notifications"
                >
                  {pushLoading ? "Enabling..." : "Enable push"}
                </button>
              )}
              {pushSupported &&
                pushEnabled &&
                notifPermission !== "granted" && (
                  <span
                    className="text-sm text-green-300"
                    aria-label="Push notifications enabled"
                  >
                    Push enabled
                  </span>
                )}
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-gray-300 hover:text-white"
                  aria-label="Mark all notifications as read"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-sm text-gray-300 hover:text-white p-1 rounded"
                aria-label="Close notifications"
              >
                {notifPermission !== "granted" ? (
                  <FaTimes aria-hidden="true" />
                ) : (
                  "Close"
                )}
              </button>
            </div>
          </div>

          {pushError && (
            <div className="text-xs text-red-400 px-2 mb-2">{pushError}</div>
          )}

          <div className="space-y-2">
            {isLoading ? (
              <div className="text-gray-400 p-2">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="text-gray-400 p-2">No notifications</div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleNotificationClick(notification);
                    }
                  }}
                  className={`p-2 rounded cursor-pointer transition-colors ${
                    notification.read
                      ? "opacity-60 hover:bg-gray-700/20"
                      : "bg-gray-700/40 hover:bg-gray-700/60"
                  }`}
                  aria-label={`Notification: ${notification.type}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="text-sm text-white flex-1">
                      {renderNotificationContent(notification)}
                      <div className="text-xs text-gray-400 mt-1">
                        {dayjs(notification.createdAt).fromNow()}
                      </div>
                    </div>
                    <div className="ml-2 flex flex-col items-end">
                      {!notification.read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification._id);
                          }}
                          className="text-xs text-blue-300 hover:text-blue-200"
                          aria-label="Mark as read"
                        >
                          Mark read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
